package dev.lingvo.site.components.studio.fonts

import dev.lingvo.shared.screenshots.text.FontCatalog
import kotlinx.browser.document
import kotlinx.coroutines.CompletableDeferred
import org.w3c.dom.HTMLLinkElement

/** On-demand catalog-only stylesheets avoid downloading every script face for every editor. */
object FontLinks {
    private data class Sheet(val link: HTMLLinkElement, val ready: CompletableDeferred<Boolean>, var loaded: Boolean = false)
    private val sheets = mutableMapOf<String, Sheet>()

    /** Returns the existing family link so concurrent renders never issue duplicate stylesheet requests. */
    fun add(family: String): HTMLLinkElement? = sheet(family)?.link

    internal suspend fun ready(family: String, retry: Boolean = false): Boolean {
        val old = sheets[family]
        // Retry an errored stylesheet, while letting a slow request keep downloading after a timeout.
        if (retry && old != null && old.ready.isCompleted && !old.ready.await()) {
            old.link.remove()
            sheets.remove(family)
        }
        return sheet(family)?.ready?.await() ?: false
    }

    internal fun loaded(family: String): Boolean = sheets[family]?.let { it.link.parentNode != null && it.loaded } == true

    /** A rejected font file must get fresh FontFace definitions on the next attempt. */
    internal fun invalidate(family: String) {
        sheets.remove(family)?.link?.remove()
    }

    private fun sheet(family: String): Sheet? {
        val query = FontCatalog.googleFontsQuery(family) ?: return null
        val existing = sheets[family]
        if (existing != null && existing.link.parentNode != null) return existing
        val link = document.createElement("link") as HTMLLinkElement
        val ready = CompletableDeferred<Boolean>()
        val sheet = Sheet(link, ready)
        link.rel = "stylesheet"
        link.href = "https://fonts.googleapis.com/css2?family=$query&display=swap"
        link.setAttribute("data-lingvo-shot-font", family)
        fun complete(ok: Boolean) {
            sheet.loaded = ok
            ready.complete(ok)
            link.onload = null
            link.onerror = null
        }
        link.onload = { complete(true); null }
        link.onerror = { _, _, _, _, _ -> complete(false); null }
        sheets[family] = sheet
        document.head?.appendChild(link)
        return sheet
    }
}
